import React, { useState, useEffect } from 'react';
import { 
  Star, 
  MapPin, 
  Phone, 
  Mail, 
  BookOpen, 
  GraduationCap, 
  Users, 
  Award,
  MessageCircle,
  Calendar,
  CheckCircle,
  X,
  ExternalLink,
  CreditCard,
  AlertCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { tuitionTeacherService, demoBookingService, teacherHiringService, teacherNegotiationService } from '../firebase/tuition';
import { useRealtime } from '../contexts/RealtimeContext';
import SmartPaymentModal from './payment/SmartPaymentModal';
import { 
  checkRecipientPayoutStatus, 
  initializeRouteCheckout, 
  calculateBreakdown,
  PLATFORM_FEE_PERCENTAGE,
  RECIPIENT_PERCENTAGE
} from '../utils/routePaymentHelper';

const TeacherProfile = ({ teacherId, onClose }) => {
  const { currentUser: user } = useRealtime();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDemoBooking, setShowDemoBooking] = useState(false);
  const [showHiringModal, setShowHiringModal] = useState(false);
  const [showNegotiationModal, setShowNegotiationModal] = useState(false);
  const [hasBookedDemo, setHasBookedDemo] = useState(false);
  const [teacherPayoutStatus, setTeacherPayoutStatus] = useState(null);
  const [isHired, setIsHired] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Helper functions to mask contact information
  const maskPhoneNumber = (phone) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 3) return phone;
    const firstThree = digits.substring(0, 3);
    const masked = '*'.repeat(digits.length - 3);
    return firstThree + masked;
  };

  const maskEmail = (email) => {
    if (!email) return '';
    const [localPart, domain] = email.split('@');
    if (!localPart || !domain) return email;
    
    if (localPart.length <= 3) {
      return localPart + '@' + domain;
    }
    
    const firstThree = localPart.substring(0, 3);
    const masked = '*'.repeat(localPart.length - 3);
    return firstThree + masked + '@' + domain;
  };

  useEffect(() => {
    if (teacherId) {
      loadTeacherProfile();
      if (user) {
        checkDemoBooking();
        checkHiringStatus();
      }
    }
  }, [teacherId, user]);

  const loadTeacherProfile = async () => {
    try {
      const teacherData = await tuitionTeacherService.getTeacher(teacherId);
      setTeacher(teacherData);
      
      // Check if teacher can receive Route payments
      if (teacherData?.userId) {
        const payoutStatus = await checkRecipientPayoutStatus(teacherData.userId);
        setTeacherPayoutStatus(payoutStatus);
      }
    } catch (error) {
      toast.error('Failed to load teacher profile');
    } finally {
      setLoading(false);
    }
  };

  const checkDemoBooking = async () => {
    if (!user || !teacherId) return;
    
    try {
      const existingBooking = await demoBookingService.getStudentDemoBooking(user.uid, teacherId);
      setHasBookedDemo(!!existingBooking);
    } catch (error) {
    }
  };

  const checkHiringStatus = async () => {
    if (!user || !teacherId) return;
    
    try {
      const studentBatches = await teacherHiringService.getStudentBatches(user.uid);
      const isHiredByStudent = studentBatches.some(batch => batch.teacherId === teacherId);
      setIsHired(isHiredByStudent);
    } catch (error) {
    }
  };

  const handleDemoBooking = async (bookingData) => {
    if (!user) {
      toast.error('Please login to book a demo class');
      return;
    }


    try {
      await demoBookingService.bookDemoClass({
        ...bookingData,
        studentId: user.uid,
        teacherId: teacherId,
        studentEmail: user.email
      });
      
      toast.success('Demo class booked successfully! The teacher will contact you soon.');
      setShowDemoBooking(false);
      setHasBookedDemo(true);
    } catch (error) {
      toast.error(error.message || 'Failed to book demo class');
    }
  };

  const handleHireTeacher = async (hiringData, negotiate = false) => {
    if (!user) {
      toast.error('Please login to hire a teacher');
      return;
    }

    // Calculate total amount based on hiring details
    const sessionCost = teacher.feesPerHour * (hiringData.duration || 1);
    const totalAmount = sessionCost; // For now, charge for one session upfront

    // If negotiating, create a negotiation instead
    if (negotiate) {
      try {
        const proposedPrice = hiringData.proposedPrice || totalAmount * 0.8; // Default to 80% of original
        
        await teacherNegotiationService.createNegotiation({
          teacherId: teacherId,
          teacherUserId: teacher.userId,
          teacherName: teacher.name,
          studentId: user.uid,
          studentName: user.displayName || user.email?.split('@')[0] || 'Student',
          studentEmail: user.email,
          originalPrice: totalAmount,
          proposedPrice: proposedPrice,
          hiringData: {
            ...hiringData,
            subjects: Array.isArray(hiringData.subjects) ? hiringData.subjects : (hiringData.subject ? [hiringData.subject] : []),
            teacherId: teacherId,
            feesPerHour: teacher.feesPerHour,
            sessionCost: sessionCost,
            totalAmount: totalAmount
          },
          status: 'pending'
        });

        toast.success('Negotiation proposal sent! The teacher will review it.');
        setShowHiringModal(false);
      } catch (error) {
        toast.error('Failed to create negotiation: ' + error.message);
      }
      return;
    }

    // Check if teacher has set up bank account for Route payments
    if (teacherPayoutStatus?.canReceive && teacher.userId) {
      // Use Razorpay Route for instant transfer to teacher
      const breakdown = calculateBreakdown(totalAmount, 'teacher_hiring');
      const subjectsText = Array.isArray(hiringData.subjects) 
        ? hiringData.subjects.join(', ') 
        : (hiringData.subject || 'Multiple subjects');

      try {
        setShowHiringModal(false);
        
        await initializeRouteCheckout({
          amount: totalAmount,
          recipientUserId: teacher.userId,
          paymentType: 'teacher_hiring',
          payerDetails: {
            name: user.displayName || user.email?.split('@')[0] || 'Student',
            email: user.email,
            phone: user.phoneNumber || ''
          },
          notes: {
            teacherId: teacherId,
            teacherName: teacher.name,
            studentId: user.uid,
            subjects: subjectsText
          },
          razorpayKeyId: process.env.REACT_APP_RAZORPAY_KEY_ID,
          onSuccess: async (response) => {
            try {
              // Complete the teacher hiring process
              const result = await teacherHiringService.hireTeacher({
                ...hiringData,
                subjects: Array.isArray(hiringData.subjects) ? hiringData.subjects : (hiringData.subject ? [hiringData.subject] : []),
                teacherId: teacherId,
                teacherName: teacher.name,
                studentId: user.uid,
                studentName: user.displayName || user.email?.split('@')[0] || 'Student',
                studentEmail: user.email,
                feesPerHour: teacher.feesPerHour,
                sessionCost: sessionCost,
                totalAmount: totalAmount,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id,
                paymentStatus: 'completed',
                isRoutePayment: true,
                platformFee: breakdown.platformFee,
                teacherAmount: breakdown.recipientAmount
              });
              
              toast.success(`Teacher hired! ₹${breakdown.recipientAmount} transferred instantly to ${teacher.name}.`);
              setIsHired(true);
            } catch (error) {
              toast.error('Payment successful but failed to complete hiring. Please contact support.');
            }
          },
          onFailure: (error) => {
            toast.error(error.error || 'Payment failed. Please try again.');
          }
        });
      } catch (error) {
        toast.error(error.message || 'Failed to initiate payment');
      }
      return;
    }

    // Fallback to regular payment if teacher hasn't set up Route
    // Prepare payment data
    const subjectsText = Array.isArray(hiringData.subjects) 
      ? hiringData.subjects.join(', ') 
      : (hiringData.subject || 'Multiple subjects');
    
    const paymentInfo = {
      type: 'teacher_hiring',
      amount: totalAmount,
      currency: 'INR',
      description: `Hire ${teacher.name} for ${subjectsText} - ${hiringData.class}`,
      merchantName: 'Mahstry',
      customerName: user.displayName || user.email?.split('@')[0] || 'Student',
      customerEmail: user.email,
      customerPhone: user.phoneNumber || '',
      userId: user.uid,
      hiringData: {
        ...hiringData,
        // Ensure subjects is always an array for backward compatibility
        subjects: Array.isArray(hiringData.subjects) ? hiringData.subjects : (hiringData.subject ? [hiringData.subject] : []),
        subject: Array.isArray(hiringData.subjects) && hiringData.subjects.length === 1 
          ? hiringData.subjects[0] 
          : (Array.isArray(hiringData.subjects) ? hiringData.subjects.join(', ') : (hiringData.subject || 'Multiple subjects')), // For display compatibility
        teacherId: teacherId,
        teacherName: teacher.name,
        studentId: user.uid,
        studentName: user.displayName || user.email?.split('@')[0] || 'Student',
        studentEmail: user.email,
        feesPerHour: teacher.feesPerHour,
        sessionCost: sessionCost,
        totalAmount: totalAmount
      }
    };

    setPaymentData(paymentInfo);
    setShowHiringModal(false);
    setShowPaymentModal(true);
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      // Complete the teacher hiring process
      const result = await teacherHiringService.hireTeacher({
        ...transactionData.hiringData,
        paymentId: transactionData.paymentId,
        paymentStatus: 'completed'
      });
      
      toast.success('Teacher hired successfully! The teacher will create a batch when ready.');
      setIsHired(true);
      setShowPaymentModal(false);
    } catch (error) {
      toast.error('Payment successful but failed to complete hiring. Please contact support.');
    }
  };

  const handlePaymentFailure = (error) => {
    toast.error('Payment failed. Please try again.');
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-center mt-4">Loading teacher profile...</p>
        </div>
      </div>
    );
  }

  if (!teacher) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-red-500">Teacher profile not found</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Teacher Profile</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:text-gray-300 dark:hover:text-gray-100 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* Header Section */}
          <div className="flex flex-col md:flex-row gap-6 mb-8">
            <div className="flex-shrink-0">
              {teacher.photoUrl ? (
                <img
                  src={teacher.photoUrl}
                  alt={teacher.name}
                  className="w-32 h-32 rounded-full object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center">
                  <GraduationCap size={48} className="text-gray-400" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{teacher.name}</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300 mb-4">
                <div className="flex items-center">
                  <MapPin size={16} className="mr-1" />
                  {[teacher.locality, teacher.city, teacher.state].filter(Boolean).join(', ')}
                </div>
                <div className="flex items-center">
                  <span className="mr-1 text-sm font-semibold">₹</span>
                  {teacher.feesPerHour}/hour
                </div>
                <div className="flex items-center">
                  <Award size={16} className="mr-1" />
                  {teacher.experience} years experience
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                {teacher.availability?.map((option) => (
                  <span
                    key={option}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {option === 'online' ? 'Online Classes' : 
                     option === 'home_visit' ? 'Home Visit' : 
                     'Student Visits'}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-4 mb-8">
            {!isHired && (
              <>
                {!hasBookedDemo && (
                  <button
                    onClick={() => setShowDemoBooking(true)}
                    className="flex items-center px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Calendar className="mr-2" size={20} />
                    Book Demo Class
                  </button>
                )}
                
                <button
                  onClick={() => setShowNegotiationModal(true)}
                  className="flex items-center px-6 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors shadow-md"
                >
                  <MessageCircle className="mr-2" size={20} />
                  Negotiate Price
                </button>
                
                <button
                  onClick={() => setShowHiringModal(true)}
                  className="flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Users className="mr-2" size={20} />
                  {hasBookedDemo ? 'Hire Teacher' : 'Hire Teacher (Skip Demo)'}
                </button>
              </>
            )}

            {isHired && (
              <div className="flex items-center px-6 py-3 bg-green-100 text-green-800 rounded-lg">
                <CheckCircle className="mr-2" size={20} />
                Teacher Hired
              </div>
            )}

          </div>

          {/* About Section */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">About</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">{teacher.bio}</p>
          </div>

          {/* Teaching Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Subjects</h3>
              <div className="flex flex-wrap gap-2">
                {teacher.subjects?.map((subject) => (
                  <span
                    key={subject}
                    className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                  >
                    {subject}
                  </span>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Classes</h3>
              <div className="flex flex-wrap gap-2">
                {teacher.classes?.map((cls) => (
                  <span
                    key={cls}
                    className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                  >
                    {cls}
                  </span>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Contact Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Mail size={20} className="text-gray-400 dark:text-gray-500 mr-3" />
                <span className="text-gray-700 dark:text-gray-300">{maskEmail(teacher.email)}</span>
              </div>
              <div className="flex items-center">
                <Phone size={20} className="text-gray-400 dark:text-gray-500 mr-3" />
                <span className="text-gray-700 dark:text-gray-300">{maskPhoneNumber(teacher.phone)}</span>
              </div>
              <div className="flex items-center">
                <MapPin size={20} className="text-gray-400 dark:text-gray-500 mr-3" />
                <span className="text-gray-700 dark:text-gray-300">
                  {[teacher.locality, teacher.city, teacher.state].filter(Boolean).join(', ')}
                </span>
              </div>
              <div className="flex items-center">
                <GraduationCap size={20} className="text-gray-400 dark:text-gray-500 mr-3" />
                <span className="text-gray-700 dark:text-gray-300">{teacher.qualification}</span>
              </div>
            </div>
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <Users size={32} className="text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{teacher.totalStudents || 0}</div>
              <div className="text-gray-600 dark:text-gray-300">Total Students</div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-6 rounded-lg text-center">
              <Award size={32} className="text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{teacher.experience}</div>
              <div className="text-gray-600 dark:text-gray-300">Years Experience</div>
            </div>
          </div>
        </div>

        {/* Demo Booking Modal */}
        {showDemoBooking && (
          <DemoBookingModal
            teacher={teacher}
            onClose={() => setShowDemoBooking(false)}
            onBook={handleDemoBooking}
          />
        )}

        {/* Negotiation Modal */}
        {showNegotiationModal && (
          <NegotiationModal
            teacher={teacher}
            teacherId={teacherId}
            onClose={() => setShowNegotiationModal(false)}
          />
        )}

        {/* Hiring Modal */}
        {showHiringModal && (
          <HiringModal
            teacher={teacher}
            onClose={() => setShowHiringModal(false)}
            onHire={handleHireTeacher}
          />
        )}
        
        {showPaymentModal && (
          <SmartPaymentModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            paymentData={paymentData}
            onSuccess={handlePaymentSuccess}
            onFailure={handlePaymentFailure}
            title="Complete Teacher Hiring Payment"
          />
        )}
      </div>
    </div>
  );
};

// Demo Booking Modal Component
const DemoBookingModal = ({ teacher, onClose, onBook }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    timeSlots: [
      { date: '', time: '' },
      { date: '', time: '' },
      { date: '', time: '' }
    ]
  });

  const handleTimeSlotChange = (index, field, value) => {
    const newTimeSlots = [...formData.timeSlots];
    newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };
    setFormData({ ...formData, timeSlots: newTimeSlots });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate that all 3 time slots are provided
    const validSlots = formData.timeSlots.filter(slot => slot.date && slot.time);
    if (validSlots.length < 3) {
      toast.error('Please provide all 3 preferred time slots');
      return;
    }
    
    onBook(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Book Demo Class</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Please provide 3 preferred time slots (teacher will choose one)
            </label>
            
            <div className="space-y-3">
              {formData.timeSlots.map((slot, index) => (
                <div key={index} className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">
                    Time Slot {index + 1}
                  </h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 mb-1 font-medium">Date</label>
                      <input
                        type="date"
                        value={slot.date}
                        onChange={(e) => handleTimeSlotChange(index, 'date', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1 font-medium">Time</label>
                      <input
                        type="time"
                        value={slot.time}
                        onChange={(e) => handleTimeSlotChange(index, 'time', e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject for Demo
            </label>
            <select
              value={formData.subject}
              onChange={(e) => setFormData({...formData, subject: e.target.value})}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select subject</option>
              {teacher.subjects?.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Additional Message
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Any specific topics you'd like to cover in the demo class?"
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
            >
              Book Demo
            </button>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

// Hiring Modal Component
const HiringModal = ({ teacher, onClose, onHire }) => {
  const [formData, setFormData] = useState({
    subjects: [],
    class: '',
    duration: '',
    frequency: '',
    startDate: '',
    message: '',
    proposedPrice: ''
  });
  const [showNegotiate, setShowNegotiate] = useState(false);

  const handleSubjectChange = (subject) => {
    setFormData(prev => {
      const currentSubjects = prev.subjects || [];
      if (currentSubjects.includes(subject)) {
        return { ...prev, subjects: currentSubjects.filter(s => s !== subject) };
      } else {
        return { ...prev, subjects: [...currentSubjects, subject] };
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.subjects || formData.subjects.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }
    onHire(formData, false);
  };

  const handleNegotiate = (e) => {
    e.preventDefault();
    if (!formData.subjects || formData.subjects.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }
    if (!formData.duration) {
      toast.error('Please enter class duration');
      return;
    }
    
    const sessionCost = teacher.feesPerHour * (formData.duration || 1);
    const proposedPrice = formData.proposedPrice 
      ? parseFloat(formData.proposedPrice) 
      : sessionCost * 0.8; // Default to 80% if not specified
    
    if (proposedPrice >= sessionCost) {
      toast.error('Proposed price must be less than the original price');
      return;
    }
    
    onHire({ ...formData, proposedPrice }, true);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 flex items-center justify-between">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Hire Teacher</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              Subjects *
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-32 overflow-y-auto p-2 border border-gray-300 rounded-md bg-gray-50">
              {teacher.subjects?.map((subject) => (
                <label key={subject} className="flex items-center cursor-pointer text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={formData.subjects?.includes(subject) || false}
                    onChange={() => handleSubjectChange(subject)}
                    className="mr-1.5 w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{subject}</span>
                </label>
              ))}
            </div>
            {formData.subjects?.length === 0 && (
              <p className="text-red-500 text-xs mt-1">Please select at least one subject</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Class *
              </label>
              <select
                value={formData.class}
                onChange={(e) => setFormData({...formData, class: e.target.value})}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select class</option>
                <option value="Nursery">Nursery</option>
                <option value="LKG">LKG</option>
                <option value="UKG">UKG</option>
                <option value="Class 1">Class 1</option>
                <option value="Class 2">Class 2</option>
                <option value="Class 3">Class 3</option>
                <option value="Class 4">Class 4</option>
                <option value="Class 5">Class 5</option>
                <option value="Class 6">Class 6</option>
                <option value="Class 7">Class 7</option>
                <option value="Class 8">Class 8</option>
                <option value="Class 9">Class 9</option>
                <option value="Class 10">Class 10</option>
                <option value="Class 11">Class 11</option>
                <option value="Class 12">Class 12</option>
                <option value="After 12th">After 12th</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Duration (hrs) *
              </label>
              <input
                type="number"
                min="1"
                max="4"
                value={formData.duration}
                onChange={(e) => setFormData({...formData, duration: e.target.value})}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Frequency *
              </label>
              <select
                value={formData.frequency}
                onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData({...formData, startDate: e.target.value})}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Additional Requirements
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              rows={2}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Any specific requirements?"
            />
          </div>

          <div className="bg-blue-50 p-2.5 sm:p-3 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs sm:text-sm font-semibold text-blue-900">
                  Session Cost: ₹{teacher.feesPerHour * (formData.duration || 1)}
                </p>
                <p className="text-xs text-blue-700">Secure payment via Razorpay</p>
              </div>
              <CreditCard className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            </div>
          </div>

          {/* Negotiate Price Option */}
          <div className="border border-yellow-300 bg-yellow-50 rounded-lg p-2.5 sm:p-3">
            <button
              type="button"
              onClick={() => setShowNegotiate(!showNegotiate)}
              className="w-full flex items-center justify-between text-left"
            >
              <div>
                <p className="text-xs sm:text-sm font-semibold text-yellow-900">💬 Negotiate Price?</p>
                <p className="text-xs text-yellow-800">Propose a lower price</p>
              </div>
              <span className="text-yellow-700 text-sm">{showNegotiate ? '▼' : '▶'}</span>
            </button>
            
            {showNegotiate && (
              <div className="mt-2 pt-2 border-t border-yellow-200">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Proposed Price (₹)
                </label>
                <input
                  type="number"
                  value={formData.proposedPrice}
                  onChange={(e) => setFormData({...formData, proposedPrice: e.target.value})}
                  placeholder={`₹${Math.round(teacher.feesPerHour * (formData.duration || 1) * 0.8)}`}
                  max={teacher.feesPerHour * (formData.duration || 1) - 1}
                  min="1"
                  className="w-full px-2 py-1.5 text-xs sm:text-sm border border-yellow-300 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500"
                />
                {formData.proposedPrice && (
                  <p className="text-xs text-green-700 mt-1 font-semibold">
                    Save: ₹{teacher.feesPerHour * (formData.duration || 1) - parseFloat(formData.proposedPrice || 0)}
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            {showNegotiate && formData.proposedPrice && (
              <button
                type="button"
                onClick={handleNegotiate}
                className="flex-1 px-3 py-2 text-xs sm:text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700 font-medium"
              >
                Send Proposal
              </button>
            )}
            <button
              type="submit"
              className="flex-1 px-3 py-2 text-xs sm:text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
            >
              Hire at Full Price
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Negotiation Modal Component
const NegotiationModal = ({ teacher, teacherId, onClose }) => {
  const { currentUser: user } = useRealtime();
  const [proposedPrice, setProposedPrice] = useState('');
  const [duration, setDuration] = useState('1');
  const [subjects, setSubjects] = useState([]);
  const [classLevel, setClassLevel] = useState('');
  const [frequency, setFrequency] = useState('');
  const [startDate, setStartDate] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const originalPrice = teacher.feesPerHour * (parseFloat(duration) || 1);
  const suggestedPrice = Math.round(originalPrice * 0.8);

  const handleSubjectChange = (subject) => {
    setSubjects(prev => {
      if (prev.includes(subject)) {
        return prev.filter(s => s !== subject);
      } else {
        return [...prev, subject];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user) {
      toast.error('Please login to negotiate price');
      return;
    }

    if (!subjects || subjects.length === 0) {
      toast.error('Please select at least one subject');
      return;
    }

    if (!classLevel) {
      toast.error('Please select a class level');
      return;
    }

    if (!duration) {
      toast.error('Please enter class duration');
      return;
    }

    if (!frequency) {
      toast.error('Please select frequency');
      return;
    }

    if (!startDate) {
      toast.error('Please select a start date');
      return;
    }

    if (!proposedPrice || parseFloat(proposedPrice) <= 0) {
      toast.error('Please enter a valid proposed price');
      return;
    }

    if (parseFloat(proposedPrice) >= originalPrice) {
      toast.error('Proposed price must be less than the original price');
      return;
    }

    setSubmitting(true);
    try {
      await teacherNegotiationService.createNegotiation({
        teacherId: teacherId,
        teacherUserId: teacher.userId,
        teacherName: teacher.name,
        studentId: user.uid,
        studentName: user.displayName || user.email?.split('@')[0] || 'Student',
        studentEmail: user.email,
        originalPrice: originalPrice,
        proposedPrice: parseFloat(proposedPrice),
        duration: duration,
        subjects: subjects,
        class: classLevel,
        frequency: frequency,
        startDate: startDate,
        message: message,
        hiringData: {
          subjects: subjects,
          class: classLevel,
          duration: duration,
          frequency: frequency,
          startDate: startDate,
          message: message,
          teacherId: teacherId,
          feesPerHour: teacher.feesPerHour,
          sessionCost: originalPrice,
          totalAmount: originalPrice
        },
        status: 'pending'
      });

      toast.success('Negotiation proposal sent! The teacher will review it and can start a chat with you.');
      onClose();
    } catch (error) {
      toast.error('Failed to send negotiation: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[95vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-4 sm:px-6 py-3 flex items-center justify-between">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900">Negotiate Price</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-3">
          {/* Price Comparison */}
          <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-3 rounded-lg border-2 border-yellow-300">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Original:</span>
              <span className="text-base sm:text-lg font-bold text-gray-900">₹{originalPrice}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-medium text-gray-700">Proposed:</span>
              <div className="flex items-center gap-1">
                <span className="text-base sm:text-lg font-bold text-yellow-700">₹</span>
                <input
                  type="number"
                  value={proposedPrice}
                  onChange={(e) => setProposedPrice(e.target.value)}
                  placeholder={suggestedPrice.toString()}
                  max={originalPrice - 1}
                  min="1"
                  className="w-20 sm:w-24 px-2 py-1 border border-yellow-300 rounded text-xs sm:text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  required
                />
              </div>
            </div>
            {proposedPrice && parseFloat(proposedPrice) > 0 && (
              <div className="mt-2 pt-2 border-t border-yellow-200">
                <span className="text-xs text-green-700 font-semibold">
                  You save: ₹{originalPrice - parseFloat(proposedPrice)}
                </span>
              </div>
            )}
          </div>

          {/* Duration and Subjects */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Duration (hrs) *
              </label>
              <input
                type="number"
                min="1"
                max="4"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5">
              Subjects * (Select at least one)
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-32 overflow-y-auto p-2 border border-gray-300 rounded-md bg-gray-50">
              {teacher.subjects?.map((subject) => (
                <label key={subject} className="flex items-center cursor-pointer text-xs sm:text-sm">
                  <input
                    type="checkbox"
                    checked={subjects.includes(subject)}
                    onChange={() => handleSubjectChange(subject)}
                    className="mr-1.5 w-3.5 h-3.5 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-gray-700">{subject}</span>
                </label>
              ))}
            </div>
            {subjects.length === 0 && (
              <p className="text-red-500 text-xs mt-1">Please select at least one subject</p>
            )}
          </div>

          {/* Class and Frequency */}
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Class *
              </label>
              <select
                value={classLevel}
                onChange={(e) => setClassLevel(e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select</option>
                <option value="Nursery">Nursery</option>
                <option value="LKG">LKG</option>
                <option value="UKG">UKG</option>
                <option value="Class 1">Class 1</option>
                <option value="Class 2">Class 2</option>
                <option value="Class 3">Class 3</option>
                <option value="Class 4">Class 4</option>
                <option value="Class 5">Class 5</option>
                <option value="Class 6">Class 6</option>
                <option value="Class 7">Class 7</option>
                <option value="Class 8">Class 8</option>
                <option value="Class 9">Class 9</option>
                <option value="Class 10">Class 10</option>
                <option value="Class 11">Class 11</option>
                <option value="Class 12">Class 12</option>
                <option value="After 12th">After 12th</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                Frequency *
              </label>
              <select
                value={frequency}
                onChange={(e) => setFrequency(e.target.value)}
                className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="">Select</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="biweekly">Bi-weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Start Date *
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
              Additional Requirements
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={2}
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Any specific requirements?"
            />
          </div>

          <div className="bg-blue-50 p-2.5 sm:p-3 rounded-lg">
            <p className="text-xs text-blue-800">
              💡 Teacher will review your proposal and can chat to finalize details.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-3 py-2 text-xs sm:text-sm border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !proposedPrice || subjects.length === 0}
              className="flex-1 px-3 py-2 text-xs sm:text-sm bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {submitting ? 'Sending...' : 'Send Negotiation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TeacherProfile;
